import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import './VerificacaoAndaime.css';

/* ============================================================
   PRODUTOS QUÍMICOS — acesso mobile (e PC)
   Consultar as fichas de dados de segurança (FDS) e as fichas técnicas
   dos produtos em uso, e registar os que ainda não estão na lista:
   tipo, nome e marca como estão no rótulo e, se quiser, a foto do rótulo.
   O escritório junta depois os PDF, que ficam no Sistema Integrado →
   Conformidade Legal → FDS. Duplicados não fazem mal — o escritório
   limpa-os. Quem registou corrige ou apaga o seu enquanto não tem PDF.
   Substitui a antiga página "Fichas Técnicas".
   ============================================================ */

// As mesmas chaves de routes/produtos-quimicos.js (TIPOS); aqui com ícone e rótulo curto
const TIPOS = [
    { v: 'cola', icone: '🧴', titulo: 'Cola' },
    { v: 'mastique', icone: '🧱', titulo: 'Mastique / selante', sub: 'cola e veda' },
    { v: 'limpeza', icone: '🧽', titulo: 'Produto de limpeza' },
    { v: 'tinta', icone: '🎨', titulo: 'Tinta / primário', sub: 'ou verniz' },
    { v: 'espuma', icone: '💨', titulo: 'Espuma PU' },
    { v: 'solvente', icone: '🧪', titulo: 'Solvente / diluente' },
    { v: 'lubrificante', icone: '🛢️', titulo: 'Óleo / lubrificante', sub: 'ou spray' },
    { v: 'outro', icone: '⚗️', titulo: 'Outro' },
];
const TIPO = Object.fromEntries(TIPOS.map(t => [t.v, t]));
const DOC_LABEL = { fds: 'FDS', ficha_tecnica: 'Ficha técnica' };

const FORM_VAZIO = { tipo: '', nome: '', marca: '', observacoes: '' };

const fmtData = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '');
// Pesquisa sem acentos nem maiúsculas: "lixivia" encontra "Lixívia" (tira as marcas
// combinantes U+0300–U+036F que a forma NFD separa das letras)
const DIACRITICOS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const normalizar = (s) => String(s || '').normalize('NFD').replace(DIACRITICOS, '').toLowerCase().trim();
// No telemóvel o PDF abre no visualizador do sistema (como na antiga página das
// fichas técnicas — é o que dá zoom e deslizar no iPhone); no PC, noutro separador.
const ehTelemovel = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent || '')
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

// Reduzir a foto no telemóvel antes de a enviar (máx. 1280 px, JPEG)
const comprimirFoto = (ficheiro) => new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Não foi possível ler a foto.'));
    leitor.onloadend = () => {
        const img = new Image();
        img.onload = () => {
            const max = 1280;
            let { width, height } = img;
            if (width > max || height > max) {
                const escala = max / Math.max(width, height);
                width = Math.round(width * escala);
                height = Math.round(height * escala);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => reject(new Error('O ficheiro escolhido não é uma imagem.'));
        img.src = leitor.result;
    };
    leitor.readAsDataURL(ficheiro);
});

const MsgErro = ({ texto }) => (texto ? <div role="alert" style={erroCampo}>{texto}</div> : null);

const ProdutosQuimicosMobile = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const veioDosEquipamentos = location.state?.from === '/equipamentos';
    const [vista, setVista] = useState('lista');        // lista | form | ok
    const [produtos, setProdutos] = useState(null);     // null = a carregar
    const [eu, setEu] = useState({ id: null, nome: '' });
    const [aCarregar, setACarregar] = useState(false);
    const [erroLista, setErroLista] = useState('');
    const [aviso, setAviso] = useState('');
    const [pesquisa, setPesquisa] = useState('');
    const [soMeus, setSoMeus] = useState(false);
    const [aAbrir, setAAbrir] = useState(null);         // id do documento a abrir
    const [fotoAberta, setFotoAberta] = useState(null); // { nome, url }

    const [form, setForm] = useState(FORM_VAZIO);
    const [emEdicao, setEmEdicao] = useState(null);     // produto a corrigir (null = novo)
    const [foto, setFoto] = useState(null);             // data URL da foto nova
    const [removerFoto, setRemoverFoto] = useState(false);
    const [aPrepararFoto, setAPrepararFoto] = useState(false);
    const [erros, setErros] = useState({});
    const [erro, setErro] = useState('');
    const [enviando, setEnviando] = useState(false);
    const aEnviar = useRef(false);
    const [gravado, setGravado] = useState(null);
    const cameraRef = useRef(null);
    const galeriaRef = useRef(null);

    const carregar = useCallback(async () => {
        setACarregar(true);
        setErroLista('');
        try {
            // _t: o Safari iOS reutilizava respostas GET antigas na PWA
            const r = await api.get('/produtos-quimicos', { params: { _t: Date.now() } });
            setProdutos(r.data.produtos || []);
            setEu(r.data.eu || { id: null, nome: '' });
        } catch (err) {
            console.error(err);
            setProdutos(prev => prev || []);
            setErroLista('Não foi possível carregar os produtos químicos.');
        } finally {
            setACarregar(false);
        }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    // Ao voltar à app, atualizar: o escritório pode ter juntado a FDS entretanto
    useEffect(() => {
        const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
        document.addEventListener('visibilitychange', aoVoltar);
        return () => document.removeEventListener('visibilitychange', aoVoltar);
    }, [carregar]);

    const abrirDocumento = async (doc) => {
        if (aAbrir) return;
        setAAbrir(doc.id);
        try {
            const r = await api.get(`/produtos-quimicos/documentos/${doc.id}`, { responseType: 'blob', timeout: 60000 });
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            if (ehTelemovel()) {
                window.location.href = url;
            } else {
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            }
        } catch {
            alert('Não foi possível abrir o documento.');
        } finally {
            setAAbrir(null);
        }
    };

    const verFoto = async (p) => {
        try {
            const r = await api.get(`/produtos-quimicos/${p.id}/foto`, { responseType: 'blob' });
            setFotoAberta({ nome: p.nome, url: URL.createObjectURL(r.data) });
        } catch {
            alert('Não foi possível abrir a foto.');
        }
    };
    const fecharFoto = () => {
        if (fotoAberta) URL.revokeObjectURL(fotoAberta.url);
        setFotoAberta(null);
    };

    const limparErro = (k) => setErros(x => {
        if (!x[k]) return x;
        const resto = { ...x };
        delete resto[k];
        return resto;
    });
    const set = (k) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [k]: v })); limparErro(k); };

    const abrirForm = (dados, produto) => {
        setForm(dados);
        setEmEdicao(produto);
        setFoto(null);
        setRemoverFoto(false);
        setErros({});
        setErro('');
        setAviso('');
        setGravado(null);
        setVista('form');
        window.scrollTo({ top: 0 });
    };
    const novo = (tipo = '') => abrirForm({ ...FORM_VAZIO, tipo }, null);
    const corrigir = (p) => abrirForm({ tipo: TIPO[p.tipo] ? p.tipo : '', nome: p.nome || '', marca: p.marca || '', observacoes: p.observacoes || '' }, p);

    const alterado = emEdicao
        ? ['tipo', 'nome', 'marca', 'observacoes'].some(k => (form[k] || '') !== (emEdicao[k] || '')) || !!foto || removerFoto
        : ['nome', 'marca', 'observacoes'].some(k => form[k].trim()) || !!foto;

    const voltarLista = () => {
        if (vista === 'form' && alterado && !window.confirm('Sair sem gravar o que preencheu?')) return;
        setVista('lista');
        setGravado(null);
        setErro('');
        setErros({});
        window.scrollTo({ top: 0 });
    };

    const escolherFoto = async (e) => {
        const ficheiro = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!ficheiro) return;
        setAPrepararFoto(true);
        setErro('');
        try {
            setFoto(await comprimirFoto(ficheiro));
            setRemoverFoto(false);
        } catch (err) {
            setErro(err.message);
        } finally {
            setAPrepararFoto(false);
        }
    };

    const validar = () => {
        const e = {};
        if (!TIPO[form.tipo]) e.tipo = 'Escolha o tipo de produto.';
        if (!form.nome.trim()) e.nome = 'Indique o nome do produto.';
        if (!form.marca.trim()) e.marca = 'Indique a marca ou o fabricante.';
        return e;
    };

    const gravar = async (e) => {
        e.preventDefault();
        if (aEnviar.current) return;
        if (aPrepararFoto) { setErro('Aguarde — a foto ainda está a ser preparada.'); return; }
        const problemas = validar();
        setErros(problemas);
        const chaves = Object.keys(problemas);
        if (chaves.length) {
            setErro('');
            document.getElementById(`pq-${chaves[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        aEnviar.current = true;
        setEnviando(true);
        setErro('');
        try {
            const dados = { ...form, ...(foto ? { foto_base64: foto } : {}), ...(removerFoto ? { remover_foto: true } : {}) };
            const r = emEdicao
                ? await api.put(`/produtos-quimicos/${emEdicao.id}`, dados, { timeout: 60000 })
                : await api.post('/produtos-quimicos', dados, { timeout: 60000 });
            setGravado({ ...r.data, corrigido: !!emEdicao });
            setVista('ok');
            carregar();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            console.error(err);
            const resp = err.response;
            if (resp?.data?.error) {
                setErro(resp.data.error);
            } else if (!resp || resp.status >= 502) {
                carregar();
                setErro('Não recebemos a confirmação do servidor — pode ter ficado gravado. Veja na lista antes de voltar a gravar (o que preencheu mantém-se aqui).');
            } else {
                setErro('Não foi possível gravar. Verifique a ligação e tente novamente.');
            }
        } finally {
            aEnviar.current = false;
            setEnviando(false);
        }
    };

    const apagar = async (p) => {
        if (!window.confirm(`Apagar o produto «${p.nome}»?`)) return;
        setAviso('');
        try {
            await api.delete(`/produtos-quimicos/${p.id}`);
            setAviso(`Produto «${p.nome}» apagado.`);
            carregar();
        } catch (err) {
            setErroLista(err.response?.data?.error || 'Não foi possível apagar o produto.');
        }
    };

    // Enter num campo de texto não grava (a tecla "Ir" do telemóvel gravava a meio)
    const bloquearEnter = (e) => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault();
    };

    const lista = produtos || [];
    const termo = normalizar(pesquisa);
    const visiveis = lista.filter(p => (!soMeus || p.registado_por === eu.id)
        && (!termo || normalizar(`${p.nome} ${p.marca} ${TIPO[p.tipo]?.titulo || ''} ${p.observacoes || ''}`).includes(termo)));
    const nMeus = lista.filter(p => p.registado_por === eu.id).length;
    const nSemFds = lista.filter(p => !p.documentos.some(d => d.tipo_documento === 'fds' && !d.requer_atualizacao)).length;

    // Parecidos com o nome escrito: se já está na lista, não é preciso registar
    const nomeEscrito = normalizar(form.nome);
    const parecidos = !emEdicao && nomeEscrito.length >= 3
        ? lista.filter(p => normalizar(p.nome).includes(nomeEscrito) || nomeEscrito.includes(normalizar(p.nome))).slice(0, 3)
        : [];

    const titulo = vista === 'form' ? (emEdicao ? '✏️ Corrigir produto' : '🧪 Registar produto') : '🧪 Produtos químicos';

    const botaoTipo = (t) => {
        const ativo = form.tipo === t.v;
        return (
            <button key={t.v} type="button" aria-pressed={ativo} onClick={() => { setForm(f => ({ ...f, tipo: t.v })); limparErro('tipo'); }} style={tipoBtn(ativo)}>
                <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">{t.icone}</span>
                <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{ativo ? '✓ ' : ''}{t.titulo}</span>
                    {t.sub && <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginTop: 1 }}>{t.sub}</span>}
                </span>
            </button>
        );
    };

    return (
        <div className="verificacao-andaimes-container">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1>{titulo}</h1>
                        <p className="mobile-hide">Fichas de dados de segurança (FDS) e fichas técnicas dos produtos em uso</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    {vista === 'lista' ? (
                        <button className="btn-secondary" style={btnTopo} onClick={() => navigate(veioDosEquipamentos ? '/equipamentos' : '/mobile-dashboard')}>← {veioDosEquipamentos ? 'Equipamentos' : 'Menu'}</button>
                    ) : (
                        <button className="btn-secondary" style={btnTopo} onClick={voltarLista}>← Voltar</button>
                    )}
                </div>
            </div>

            <div className="page-content" style={{ padding: 16 }}>
                <div className="va-content-wrapper">
                    {vista === 'lista' && (
                        /* ---------- LISTA / CONSULTA ---------- */
                        <>
                            <button type="button" onClick={() => novo()} style={btnRegistar}>
                                <span style={iconeRegistar} aria-hidden="true">+</span>
                                <span style={{ minWidth: 0 }}>
                                    <span style={{ display: 'block', fontSize: 17, fontWeight: 800 }}>Registar produto</span>
                                    <span style={{ display: 'block', fontSize: 13, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>
                                        Colas, mastiques, cola e veda, limpeza… que ainda não estejam na lista
                                    </span>
                                </span>
                            </button>

                            <input type="search" value={pesquisa} onChange={e => setPesquisa(e.target.value)}
                                placeholder="Procurar produto ou marca…" aria-label="Procurar produto ou marca"
                                style={{ ...inp, marginBottom: 10 }} />

                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => setSoMeus(false)} style={chip(!soMeus)}>Todos ({lista.length})</button>
                                <button type="button" onClick={() => setSoMeus(true)} style={chip(soMeus)}>Registados por mim ({nMeus})</button>
                                <button type="button" onClick={carregar} disabled={aCarregar} style={{ ...btnMini, marginLeft: 'auto' }}>
                                    {aCarregar ? 'A atualizar…' : '🔄'}
                                </button>
                            </div>
                            {nSemFds > 0 && !termo && !soMeus && (
                                <div style={{ fontSize: 13, color: '#c05621', margin: '-4px 0 10px' }}>
                                    ⏳ {nSemFds === 1 ? '1 produto com FDS em falta ou por atualizar' : `${nSemFds} produtos com FDS em falta ou por atualizar`} — o escritório está a tratar.
                                </div>
                            )}

                            {aviso && <div className="va-message success">{aviso}</div>}
                            {erroLista && <div className="va-message error">{erroLista}</div>}

                            {produtos === null ? (
                                <div style={vazio}>A carregar…</div>
                            ) : visiveis.length === 0 ? (
                                <div style={vazio}>
                                    {termo || soMeus
                                        ? <>Nenhum produto encontrado.<br />Se não está na lista, toque em «Registar produto».</>
                                        : <>Ainda não há produtos registados.<br />Toque em «Registar produto» para começar.</>}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {visiveis.map(p => {
                                        const t = TIPO[p.tipo] || TIPO.outro;
                                        const temFds = p.documentos.some(d => d.tipo_documento === 'fds' && !d.requer_atualizacao);
                                        const porTipo = (tipo) => p.documentos.filter(d => d.tipo_documento === tipo);
                                        return (
                                            <div key={p.id} style={{ ...cartao, marginBottom: 0, borderLeft: `4px solid ${temFds ? '#38a169' : '#dd6b20'}` }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={badge('#edf2f7', '#2d3748')}>{t.icone} {t.titulo}</span>
                                                    {temFds
                                                        ? <span style={{ ...badge('#f0fff4', '#276749'), marginLeft: 'auto' }}>✅ Com FDS</span>
                                                        : <span style={{ ...badge('#fffaf0', '#c05621'), marginLeft: 'auto' }}>⏳ FDS pendente</span>}
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: 17, color: '#1a202c', marginTop: 8, wordBreak: 'break-word' }}>{p.nome}</div>
                                                <div style={{ fontSize: 14, color: '#4a5568', marginTop: 2 }}>{p.marca}</div>
                                                {p.observacoes && (
                                                    <div style={{ fontSize: 13, color: '#718096', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.observacoes}</div>
                                                )}
                                                {p.documentos.length > 0 ? (
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                                        {['fds', 'ficha_tecnica'].flatMap(tipo => porTipo(tipo).map((d, i, todos) => (
                                                            <button key={d.id} type="button" onClick={() => abrirDocumento(d)} disabled={!!aAbrir}
                                                                style={tipo === 'fds' ? btnDocFds : btnDocFt}>
                                                                {aAbrir === d.id ? 'A abrir…' : `${tipo === 'fds' ? '📄' : '📘'} ${DOC_LABEL[tipo]}`}
                                                                {d.requer_atualizacao ? ' · Atualização por pedir' : ''}
                                                                {todos.length > 1 && d.data_documento ? ` · ${new Date(d.data_documento).getFullYear()}` : ''}
                                                            </button>
                                                        )))}
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 13, color: '#975a16', background: '#fffaf0', borderRadius: 8, padding: '8px 10px', marginTop: 10 }}>
                                                        O escritório vai juntar a FDS e a ficha técnica deste produto.
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: 12, color: '#718096', flex: 1, minWidth: 150 }}>
                                                        Registado por {p.registado_por_nome || '—'} · {fmtData(p.criado_em)}
                                                    </span>
                                                    {p.tem_foto && <button type="button" style={btnMini} onClick={() => verFoto(p)}>📷 Rótulo</button>}
                                                </div>
                                                {p.pode_editar && p.registado_por === eu.id && (
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                                        <button type="button" style={btnAcao} onClick={() => corrigir(p)}>✏️ Corrigir</button>
                                                        <button type="button" style={btnApagar} onClick={() => apagar(p)}><DeleteIcon /> Apagar</button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {vista === 'ok' && gravado && (
                        /* ---------- GRAVADO ---------- */
                        <div className="va-success-actions">
                            <div style={{ fontSize: 44, lineHeight: 1 }}>✅</div>
                            <p className="success-text" style={{ margin: '12px 0 6px' }}>{gravado.corrigido ? 'Produto corrigido' : 'Produto registado'}</p>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a202c', wordBreak: 'break-word' }}>{gravado.nome}</div>
                            <div style={{ color: '#4a5568', marginTop: 4 }}>{gravado.marca}</div>
                            <p style={{ color: '#4a5568', margin: '14px 0 16px', lineHeight: 1.45 }}>
                                O escritório vai juntar a <b>ficha de dados de segurança (FDS)</b> e a <b>ficha técnica</b>.
                                Depois pode consultá-las aqui, na lista.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <button type="button" className="va-action-btn" onClick={() => novo()}
                                    style={{ background: '#0056b3', color: '#fff', width: '100%', padding: '14px 20px' }}>
                                    + Registar outro
                                </button>
                                <button type="button" className="va-action-btn secondary-btn" onClick={voltarLista}
                                    style={{ width: '100%', padding: '14px 20px' }}>
                                    📋 Ver produtos
                                </button>
                            </div>
                        </div>
                    )}

                    {vista === 'form' && (
                        /* ---------- REGISTAR / CORRIGIR ---------- */
                        <form className="va-form" onSubmit={gravar} onKeyDown={bloquearEnter} noValidate>
                            <div id="pq-tipo" role="group" aria-labelledby="pq-tit-tipo" style={cartaoE(!!erros.tipo)}>
                                <div id="pq-tit-tipo" style={secTit}>Que tipo de produto é? *</div>
                                <div style={grelha2}>{TIPOS.map(botaoTipo)}</div>
                                <MsgErro texto={erros.tipo} />
                            </div>

                            <div style={cartao}>
                                <label htmlFor="pq-nome" style={rotulo}>Nome do produto *</label>
                                <input id="pq-nome" value={form.nome} onChange={set('nome')} maxLength={150}
                                    placeholder="Ex.: Soudaflex 40FC" autoComplete="off"
                                    style={{ ...inp, ...(erros.nome ? inpComErro : {}) }} />
                                <div style={dica}>Como está escrito no rótulo da embalagem.</div>
                                <MsgErro texto={erros.nome} />
                                {parecidos.length > 0 && (
                                    <div style={notaParecidos}>
                                        <b>Já na lista:</b> {parecidos.map(p => `${p.nome} (${p.marca})`).join(' · ')}.
                                        Se é o mesmo produto, não precisa de o registar.
                                    </div>
                                )}

                                <label htmlFor="pq-marca" style={{ ...rotulo, marginTop: 14 }}>Marca / fabricante *</label>
                                <input id="pq-marca" value={form.marca} onChange={set('marca')} maxLength={100}
                                    placeholder="Ex.: Soudal" autoComplete="off" autoCapitalize="words"
                                    style={{ ...inp, ...(erros.marca ? inpComErro : {}) }} />
                                <MsgErro texto={erros.marca} />
                            </div>

                            <div style={cartao}>
                                <div style={secTit}>Foto do rótulo <span style={opcional}>(opcional)</span></div>
                                <div style={{ ...dica, marginTop: -4, marginBottom: 10 }}>Ajuda o escritório a encontrar a ficha certa.</div>
                                {foto ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <img src={foto} alt="Foto do rótulo" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                                        <button type="button" style={btnApagar} onClick={() => setFoto(null)}><DeleteIcon /> Tirar a foto</button>
                                    </div>
                                ) : emEdicao?.tem_foto && !removerFoto ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 14, color: '#2d3748' }}>📷 Já tem foto do rótulo.</span>
                                        <button type="button" style={btnMini} onClick={() => verFoto(emEdicao)}>Ver</button>
                                        <button type="button" style={btnMini} onClick={() => setRemoverFoto(true)}><DeleteIcon /> Remover</button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button type="button" className="va-action-btn" disabled={aPrepararFoto}
                                            onClick={() => cameraRef.current?.click()} style={btnFoto('#2b6cb0')}>
                                            {aPrepararFoto ? 'A preparar…' : '📷 Tirar foto'}
                                        </button>
                                        <button type="button" className="va-action-btn" disabled={aPrepararFoto}
                                            onClick={() => galeriaRef.current?.click()} style={btnFoto('#4a5568')}>
                                            🖼️ Galeria
                                        </button>
                                    </div>
                                )}
                                {emEdicao?.tem_foto && removerFoto && !foto && (
                                    <div style={{ ...dica, marginTop: 8 }}>A foto atual vai ser removida. <button type="button" onClick={() => setRemoverFoto(false)} style={linkBtn}>Manter</button></div>
                                )}
                                <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={escolherFoto} style={{ display: 'none' }} />
                                <input ref={galeriaRef} type="file" accept="image/*" onChange={escolherFoto} style={{ display: 'none' }} />
                            </div>

                            <div style={cartao}>
                                <label htmlFor="pq-obs" style={secTitRotulo}>Onde é usado? <span style={opcional}>(opcional)</span></label>
                                <textarea id="pq-obs" value={form.observacoes} onChange={set('observacoes')} rows={2} maxLength={1000}
                                    placeholder="Ex.: caixilharia, casas de banho, armazém"
                                    style={{ ...inp, minHeight: 64, resize: 'vertical' }} />
                            </div>

                            {eu.nome && !emEdicao && <div style={notaInfo}>👤 Fica registado em nome de <b>{eu.nome}</b>.</div>}
                            {erro && <div className="va-message error" role="alert">{erro}</div>}

                            <div className="va-actions" style={{ marginTop: 6 }}>
                                <button type="submit" className="va-submit-btn" disabled={enviando || aPrepararFoto}
                                    style={{ padding: '16px 20px', width: '100%' }}>
                                    {enviando ? 'A gravar…' : emEdicao ? '💾 Gravar correção' : '💾 Registar'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {fotoAberta && (
                <div role="dialog" aria-label={`Rótulo de ${fotoAberta.nome}`} onClick={fecharFoto} style={fundoFoto}>
                    <img src={fotoAberta.url} alt={`Rótulo de ${fotoAberta.nome}`} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 10 }} />
                    <button type="button" onClick={fecharFoto} style={{ ...btnMini, marginTop: 12 }}>Fechar</button>
                </div>
            )}
        </div>
    );
};

export default ProdutosQuimicosMobile;

/* ---------- estilos (os mesmos do registo de ferramentas) ---------- */
const cartao = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
const cartaoE = (comErro) => (comErro ? { ...cartao, border: '1px solid #fc8181', boxShadow: '0 0 0 1px #fc8181' } : cartao);
const secTit = { fontWeight: 700, fontSize: 15, color: '#1a202c', marginBottom: 10 };
const secTitRotulo = { ...secTit, display: 'block' };
const rotulo = { display: 'block', fontWeight: 700, marginBottom: 8, fontSize: 15, color: '#1a202c' };
const opcional = { fontWeight: 400, fontSize: 13, color: '#718096' };
const dica = { fontSize: 12, color: '#718096', marginTop: 6, lineHeight: 1.35 };
const grelha2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 };
// fontSize 16: abaixo disso o Safari do iPhone faz zoom ao tocar no campo
const inp = {
    display: 'block', width: '100%', minWidth: 0, boxSizing: 'border-box', minHeight: 44, fontSize: 16,
    padding: '10px 12px', border: '1px solid #cbd5e0', borderRadius: 8, background: '#fff', color: '#2d3748', fontFamily: 'inherit',
};
const inpComErro = { border: '1px solid #e53e3e', boxShadow: '0 0 0 1px #e53e3e' };
const erroCampo = { color: '#c53030', fontSize: 13, fontWeight: 600, marginTop: 6, lineHeight: 1.35 };
const chip = (ativo) => ({
    minHeight: 40, padding: '8px 14px', borderRadius: 20, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
    border: '1px solid ' + (ativo ? '#2b6cb0' : '#cbd5e0'), background: ativo ? '#2b6cb0' : '#fff', color: ativo ? '#fff' : '#334155',
});
const tipoBtn = (ativo) => ({
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0, minHeight: 56, textAlign: 'left',
    padding: '8px 10px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', wordBreak: 'break-word',
    border: `2px solid ${ativo ? '#2b6cb0' : '#e2e8f0'}`, background: ativo ? '#ebf8ff' : '#fff', color: ativo ? '#1e4e8c' : '#2d3748',
});
const badge = (bg, fg) => ({ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' });
const btnTopo = { minHeight: 44, whiteSpace: 'nowrap' };
const btnRegistar = {
    display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', cursor: 'pointer',
    padding: '16px 18px', marginBottom: 14, border: 'none', borderRadius: 14, fontFamily: 'inherit',
    background: 'linear-gradient(135deg, #3182ce 0%, #2563a8 100%)', color: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
};
const iconeRegistar = {
    width: 44, height: 44, flexShrink: 0, borderRadius: 12, background: 'rgba(255,255,255,0.22)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 700, lineHeight: 1,
};
const btnMini = { border: '1px solid #cbd5e0', background: '#fff', borderRadius: 8, padding: '8px 12px', minHeight: 40, fontSize: 13, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' };
const btnAcao = { flex: 1, minHeight: 44, border: '1px solid #cbd5e0', background: '#fff', color: '#2d3748', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
const btnApagar = { ...btnAcao, border: '1px solid #feb2b2', background: '#fff5f5', color: '#c53030' };
const btnDocFds = { minHeight: 44, padding: '8px 14px', borderRadius: 10, border: 'none', background: '#c53030', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
const btnDocFt = { ...btnDocFds, background: '#2b6cb0' };
const btnFoto = (cor) => ({ flex: 1, minHeight: 48, background: cor, color: '#fff', padding: '12px 0' });
const linkBtn = { border: 'none', background: 'none', color: '#2b6cb0', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' };
const notaInfo = { background: '#f7fafc', border: '1px solid #e2e8f0', color: '#4a5568', borderRadius: 10, padding: '10px 12px', fontSize: 14, lineHeight: 1.45, marginBottom: 14 };
const notaParecidos = { background: '#ebf8ff', border: '1px solid #bee3f8', color: '#2a4365', borderRadius: 8, padding: '8px 10px', fontSize: 13, lineHeight: 1.4, marginTop: 8 };
const vazio = { color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '28px 0', lineHeight: 1.5 };
const fundoFoto = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 };
